import { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Stepper } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';

import { ComparisonGrid } from './comparison-grid';
import { defaultWizardConfig } from './config';
import { MassEditWizardProps } from './types';
import { generateComparisonData, hasChanges } from './utils';

export const MassEditWizard = ({
  rows,
  children,
  getFormValues,
  validateForm,
  config = defaultWizardConfig,
  onSubmit,
  onSuccess,
  onError,
  onCancel,
  title = 'Bulk Edit',
  step1Header = 'Edit Form',
  step2Header = 'Review Changes',
  successMessage,
  isSubmitting = false,
}: MassEditWizardProps) => {
  const dispatch = useDispatch();
  const stepperRef = useRef<any>(null);
  const [isInternalSubmitting, setIsInternalSubmitting] = useState(false);

  const submitting = isSubmitting || isInternalSubmitting;

  const formValues = getFormValues();

  const { fields, getRowDisplayName, excludeKeys = ['id'] } = config;

  const comparisonData = useMemo(() => {
    if (!formValues) return [];
    return generateComparisonData(formValues, rows, fields, excludeKeys);
  }, [formValues, rows, fields, excludeKeys]);

  const hasAnyChanges = useMemo(
    () => hasChanges(comparisonData),
    [comparisonData],
  );

  const handleApiResults = useCallback(
    (
      results: Array<{ success: boolean; message?: string }>,
      defaultSuccessMessage: string,
    ) => {
      const failedResults = results.filter((r) => r.success === false);

      if (failedResults.length > 0) {
        failedResults.forEach((result) => {
          dispatch(
            addToast({
              severity: 'warn',
              summary: 'Error',
              detail: result.message || 'Failed to update records',
              life: ToastLife.MEDIUM,
            }),
          );
        });
      } else {
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Success',
            detail: successMessage || defaultSuccessMessage,
            life: ToastLife.MEDIUM,
          }),
        );
      }
    },
    [dispatch, successMessage],
  );

  const handleNext = useCallback(async () => {
    if (validateForm) {
      const isFormValid = await validateForm();
      if (isFormValid) {
        stepperRef.current?.nextCallback?.();
      }
    } else {
      stepperRef.current?.nextCallback?.();
    }
  }, [validateForm]);

  const handleBack = useCallback(() => {
    stepperRef.current?.prevCallback?.();
  }, []);

  const handleConfirm = useCallback(async () => {
    const currentFormValues = getFormValues();
    if (!currentFormValues) return;

    setIsInternalSubmitting(true);
    try {
      const results = await onSubmit(currentFormValues, rows);
      handleApiResults(
        results,
        `Successfully updated ${rows.length} record(s)`,
      );

      if (results.every((r) => r.success)) {
        onSuccess?.(currentFormValues);
        onCancel();
      }
    } catch (error) {
      console.error('Failed to update records:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update records',
          life: ToastLife.MEDIUM,
        }),
      );
      onError?.(error);
    } finally {
      setIsInternalSubmitting(false);
    }
  }, [
    getFormValues,
    rows,
    onSubmit,
    handleApiResults,
    onSuccess,
    onCancel,
    onError,
    dispatch,
  ]);

  return (
    <div className="flex h-full flex-col">
      <Stepper
        ref={stepperRef}
        style={{ flexBasis: '50rem', width: '100%' }}
        data-testid="stepper"
      >
        <StepperPanel header={step1Header}>
          <div className="flex-column flex">
            <div className="flex-1 overflow-auto">{children}</div>
          </div>
          <div className="justify-content-end flex pt-4">
            <Button
              label="Next"
              icon="pi pi-arrow-right"
              iconPos="right"
              onClick={handleNext}
              disabled={submitting}
            />
          </div>
        </StepperPanel>
        <StepperPanel header={step2Header}>
          <div className="flex w-full flex-col">
            <div className="mb-4 w-full rounded bg-blue-50 p-4">
              <p className="text-sm text-gray-700">
                Review the changes below. Changed fields are highlighted in{' '}
                <span className="rounded bg-red-100 px-2 py-1">red (old)</span>{' '}
                and{' '}
                <span className="rounded bg-green-100 px-2 py-1">
                  green (new)
                </span>
                .
              </p>
            </div>
            <ComparisonGrid
              comparisonData={comparisonData}
              fields={fields}
              getRowDisplayName={getRowDisplayName}
            />
          </div>
          <div className="justify-content-between flex gap-2 pt-4">
            <Button
              label="Back"
              severity="secondary"
              icon="pi pi-arrow-left"
              onClick={handleBack}
              disabled={submitting}
            />
            <Button
              label="Confirm Changes"
              icon="pi pi-check"
              severity="success"
              onClick={handleConfirm}
              loading={submitting}
              disabled={!hasAnyChanges}
            />
          </div>
        </StepperPanel>
      </Stepper>
    </div>
  );
};
